import { supabase } from '../supabaseClient';

const FRIENDLY_ERROR_MAP = [
  { key: 'not assigned to venue', message: 'You are not assigned to this venue. Please contact your manager.' },
  { key: 'already have an active shift', message: 'You are already clocked in.' },
  { key: 'no active shift', message: 'No active shift found.' },
  { key: 'active break already exists', message: 'You already have an active break.' },
  { key: 'no active break', message: 'No active break found.' },
  { key: 'break already ended', message: 'This break is already ended.' },
  { key: 'cannot clock out while break is active', message: 'End your break before clocking out.' },
  { key: 'permission denied', message: 'You do not have permission for this action.' },
];

const pickString = (obj, keys, fallback = null) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return fallback;
};

const pickNumber = (obj, keys, fallback = 0) => {
  for (const key of keys) {
    const raw = obj?.[key];
    const val = Number(raw);
    if (Number.isFinite(val)) return val;
  }
  return fallback;
};

const pickBoolean = (obj, keys, fallback = false) => {
  for (const key of keys) {
    const raw = obj?.[key];
    if (typeof raw === 'boolean') return raw;
    if (raw === 1 || raw === '1' || raw === 'true') return true;
    if (raw === 0 || raw === '0' || raw === 'false') return false;
  }
  return fallback;
};

const normalizeErrorMessage = (error, fallback = 'Something went wrong') => {
  const raw = error?.message || error?.details || fallback;
  const lower = String(raw).toLowerCase();
  const match = FRIENDLY_ERROR_MAP.find((item) => lower.includes(item.key));
  return match ? match.message : raw;
};

const throwIfError = (error, fallbackMessage) => {
  if (!error) return;
  const friendly = normalizeErrorMessage(error, fallbackMessage);
  const wrapped = new Error(friendly);
  wrapped.original = error;
  throw wrapped;
};

const toIsoTimestamp = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
};

const mapAssignedVenue = (row) => {
  const venue = row?.venues || {};
  return {
    id: pickString(venue, ['id']) || pickString(row, ['venue_id']) || '',
    name: pickString(venue, ['name'], 'Unnamed Venue'),
    address: pickString(venue, ['address']),
    logo_url: pickString(venue, ['logo_url']),
  };
};

const mapTimeEntry = (row) => ({
  id: pickString(row, ['id', 'time_entry_id'], ''),
  user_id: pickString(row, ['user_id'], ''),
  venue_id: pickString(row, ['venue_id'], ''),
  venue_name: pickString(row, ['venue_name']),
  clock_in_at: pickString(row, ['clock_in_at', 'clock_in_time']),
  clock_out_at: pickString(row, ['clock_out_at', 'clock_out_time']),
  break_start_at: pickString(row, ['break_start_at', 'break_start_time']),
  break_end_at: pickString(row, ['break_end_at', 'break_end_time']),
  status: pickString(row, ['status'], 'open'),
  has_active_break: pickBoolean(row, ['has_active_break', 'on_break'], false),
  break_minutes: pickNumber(row, ['break_minutes', 'total_break_minutes'], 0),
  paid_hours: pickNumber(row, ['paid_hours'], 0),
  source: pickString(row, ['source']),
  notes: pickString(row, ['notes']),
  shift_date: pickString(row, ['shift_date']),
  role: pickString(row, ['role']),
  staff_name: pickString(row, ['staff_name', 'user_name', 'full_name', 'name']),
  edited: pickBoolean(row, ['edited', 'is_edited'], false),
  raw: row,
});

const mapLiveAttendance = (row) => ({
  time_entry_id: pickString(row, ['time_entry_id', 'id'], ''),
  user_id: pickString(row, ['user_id'], ''),
  staff_name: pickString(row, ['staff_name', 'full_name', 'user_name', 'name'], 'Unknown Staff'),
  role: pickString(row, ['role'], 'staff'),
  venue_id: pickString(row, ['venue_id'], ''),
  venue_name: pickString(row, ['venue_name'], 'Unknown Venue'),
  clock_in_at: pickString(row, ['clock_in_at', 'clock_in_time']),
  break_start_at: pickString(row, ['break_start_at', 'break_start_time']),
  break_end_at: pickString(row, ['break_end_at', 'break_end_time']),
  on_break: pickBoolean(row, ['on_break', 'has_active_break'], false),
  source: pickString(row, ['source'], 'mobile'),
  notes: pickString(row, ['notes']),
  raw: row,
});

const mapPayrollSummaryRow = (row) => ({
  user_id: pickString(row, ['user_id'], ''),
  staff_name: pickString(row, ['staff_name', 'full_name', 'user_name', 'name'], 'Unknown Staff'),
  role: pickString(row, ['role'], 'staff'),
  shift_count: pickNumber(row, ['shift_count', 'total_shifts'], 0),
  total_paid_hours: pickNumber(row, ['total_paid_hours', 'paid_hours'], 0),
  raw: row,
});

const mapPayrollVenueTotal = (row) => ({
  venue_id: pickString(row, ['venue_id'], ''),
  venue_name: pickString(row, ['venue_name'], 'Unknown Venue'),
  week_start_date: pickString(row, ['week_start_date', 'week_start']),
  total_paid_hours: pickNumber(row, ['total_paid_hours', 'paid_hours'], 0),
  total_shifts: pickNumber(row, ['total_shifts', 'shift_count'], 0),
  staff_count: pickNumber(row, ['staff_count'], 0),
  raw: row,
});

const applyCommonFilters = (query, filters = {}) => {
  let q = query;

  if (filters.venueId) q = q.eq('venue_id', filters.venueId);
  if (filters.staffId) q = q.eq('user_id', filters.staffId);
  if (filters.role) q = q.eq('role', filters.role);
  if (filters.status) q = q.eq('status', filters.status);
  if (filters.search) q = q.ilike('staff_name', `%${filters.search}%`);

  return q;
};

export const attendanceService = {
  mapErrorMessage(error, fallback) {
    return normalizeErrorMessage(error, fallback);
  },

  async getAssignedVenuesForCurrentUser(userId) {
    const { data, error } = await supabase
      .from('user_venues')
      .select('venue_id, venues(id, name, address, logo_url)')
      .eq('user_id', userId)
      .order('venue_id', { ascending: true });

    throwIfError(error, 'Failed to load assigned venues');
    return (data || []).map(mapAssignedVenue).filter((v) => v.id);
  },

  async getAccessibleVenuesForUser(user) {
    const canSeeAllVenues = user?.role === 'super_admin' || user?.role === 'ops';

    if (canSeeAllVenues) {
      const { data, error } = await supabase
        .from('venues')
        .select('id, name, address, logo_url')
        .order('name', { ascending: true });

      throwIfError(error, 'Failed to load venues');
      return (data || []).map((venue) => ({
        id: venue.id,
        name: venue.name || 'Unnamed Venue',
        address: venue.address || null,
        logo_url: venue.logo_url || null,
      }));
    }

    return this.getAssignedVenuesForCurrentUser(user?.id);
  },

  async getStaffDirectory() {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, role')
      .order('name', { ascending: true });

    throwIfError(error, 'Failed to load staff directory');

    return (data || []).map((member) => ({
      id: member.id,
      name: member.name || 'Unknown',
      role: member.role || 'staff',
    }));
  },

  async getMyActiveShift(userId) {
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .is('clock_out_at', null)
      .order('clock_in_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    throwIfError(error, 'Failed to load active shift');
    return data ? mapTimeEntry(data) : null;
  },

  async getMyRecentShifts(userId, limit = 14) {
    const { data, error } = await supabase
      .from('v_time_entry_details')
      .select('*')
      .eq('user_id', userId)
      .order('shift_date', { ascending: false })
      .limit(limit);

    throwIfError(error, 'Failed to load shift history');
    return (data || []).map(mapTimeEntry);
  },

  async clockIn(venueId) {
    const { data, error } = await supabase.rpc('clock_in', {
      p_venue_id: venueId,
      p_source: 'mobile',
    });
    throwIfError(error, 'Clock in failed');
    return data;
  },

  async startBreak() {
    const { data, error } = await supabase.rpc('start_break');
    throwIfError(error, 'Unable to start break');
    return data;
  },

  async endBreak() {
    const { data, error } = await supabase.rpc('end_break');
    throwIfError(error, 'Unable to end break');
    return data;
  },

  async clockOut() {
    const { data, error } = await supabase.rpc('clock_out');
    throwIfError(error, 'Clock out failed');
    return data;
  },

  async getLiveAttendance(filters = {}) {
    let query = supabase
      .from('v_live_attendance')
      .select('*')
      .order('clock_in_at', { ascending: false });

    query = applyCommonFilters(query, filters);

    const { data, error } = await query;
    throwIfError(error, 'Failed to load live attendance');
    return (data || []).map(mapLiveAttendance);
  },

  async getWeeklyTimeEntries(filters) {
    const weekStartDate = new Date(filters.weekStart);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekStartDate.getDate() + 6);
    const defaultEndISO = weekEndDate.toISOString().slice(0, 10);

    const shiftDateFrom = filters.dateFrom || filters.weekStart;
    const shiftDateTo = filters.dateTo || defaultEndISO;

    let query = supabase
      .from('v_time_entry_details')
      .select('*')
      .gte('shift_date', shiftDateFrom)
      .lte('shift_date', shiftDateTo)
      .order('shift_date', { ascending: false })
      .order('clock_in_at', { ascending: false });

    query = applyCommonFilters(query, filters);

    const { data, error } = await query;
    throwIfError(error, 'Failed to load weekly time entries');
    return (data || []).map(mapTimeEntry);
  },

  async getPayrollSummary(filters) {
    let summaryQuery = supabase
      .from('v_payroll_weekly_summary')
      .select('*')
      .eq('week_start_date', filters.weekStart)
      .order('staff_name', { ascending: true });

    let totalsQuery = supabase
      .from('v_payroll_weekly_venue_totals')
      .select('*')
      .eq('week_start_date', filters.weekStart)
      .order('venue_name', { ascending: true });

    summaryQuery = applyCommonFilters(summaryQuery, filters);
    totalsQuery = applyCommonFilters(totalsQuery, filters);

    const [{ data: summaryData, error: summaryError }, { data: totalData, error: totalError }] = await Promise.all([
      summaryQuery,
      totalsQuery,
    ]);

    throwIfError(summaryError, 'Failed to load payroll summary');
    throwIfError(totalError, 'Failed to load payroll totals');

    return {
      summary: (summaryData || []).map(mapPayrollSummaryRow),
      totals: (totalData || []).map(mapPayrollVenueTotal),
    };
  },

  async editTimeEntry(payload) {
    const { data, error } = await supabase.rpc('manager_edit_time_entry', {
      p_time_entry_id: payload.timeEntryId,
      p_clock_in_at: toIsoTimestamp(payload.newClockInAt),
      p_clock_out_at: toIsoTimestamp(payload.newClockOutAt),
      p_notes: payload.notes || null,
      p_reason: payload.reason,
    });

    throwIfError(error, 'Failed to update shift');
    return data;
  },

  async createTimeEntry(payload) {
    const { data, error } = await supabase.rpc('manager_create_time_entry', {
      p_user_id: payload.userId,
      p_venue_id: payload.venueId,
      p_clock_in_at: toIsoTimestamp(payload.clockInAt),
      p_clock_out_at: toIsoTimestamp(payload.clockOutAt),
      p_notes: payload.notes || null,
      p_source: payload.source || 'manager',
    });

    throwIfError(error, 'Failed to create shift');
    return data;
  },
};
